// =============================================================================
// components/voice/ParticipantSheet.tsx — tap-a-participant action sheet.
//
// Opened from a VoiceRoomScreen tile tap. Only actions with a REAL backing are
// wired:
//   • Mute for me  — local-only volume zero on the RemoteParticipant
//     (livekit-client RemoteParticipant.setVolume; no server call needed),
//   • Report       — reuses the existing moderation ReportBlockSheet
//     (services/moderation reportUser accepts the user id we carry in the
//     LiveKit participant metadata).
// "View profile" is intentionally NOT offered here: custom-room tokens carry
// only `user_<id>` identities + a userId in metadata, and the UserProfile route
// requires a username (GET /users/profile/:username) — no resolvable backend.
// =============================================================================
import React, { useMemo, useState, useCallback } from 'react';
import { Modal, View, Text, Pressable, StyleSheet } from 'react-native';
import { RemoteParticipant, type Participant } from 'livekit-client';
import { Ionicons } from '../ui/Icon';
import ReportBlockSheet, { type ReportTarget } from '../moderation/ReportBlockSheet';
import { colors, spacing, radius, fonts } from '../../constants/theme';

interface Props {
  visible: boolean;
  participant: Participant | null;
  onClose: () => void;
}

/** Parse the userId our API embeds in the participant token metadata. */
function participantUserId(p: Participant | null): string | null {
  if (!p) return null;
  try {
    const meta = p.metadata ? JSON.parse(p.metadata) : null;
    if (meta && typeof meta.userId === 'string' && meta.userId) return meta.userId;
  } catch {}
  // Fallback: identities are minted as `user_<id>` (apps/api voice.ts).
  const ident = p.identity || '';
  if (ident.startsWith('user_') && ident.length > 5) return ident.slice(5);
  return null;
}

export default function ParticipantSheet({ visible, participant, onClose }: Props) {
  const isRemote = participant instanceof RemoteParticipant;
  const displayName = participant?.name || participant?.identity || 'User';
  const userId = useMemo(() => participantUserId(participant), [participant]);

  // Local-only mute; read the live volume so reopening the sheet stays in sync.
  const mutedForMe = isRemote && (participant as RemoteParticipant).getVolume() === 0;
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);

  const toggleMuteForMe = useCallback(() => {
    if (!isRemote) return;
    try {
      (participant as RemoteParticipant).setVolume(mutedForMe ? 1 : 0);
    } catch (e) {
      console.warn('[voice] setVolume failed:', e);
    }
    onClose();
  }, [isRemote, participant, mutedForMe, onClose]);

  const openReport = useCallback(() => {
    if (!userId) return;
    setReportTarget({ type: 'user', authorId: userId, authorLabel: displayName, allowMute: false });
  }, [userId, displayName]);

  const closeReport = useCallback(() => {
    setReportTarget(null);
    onClose();
  }, [onClose]);

  // The moderation sheet takes over once a report target is set.
  if (reportTarget) {
    return <ReportBlockSheet visible target={reportTarget} onClose={closeReport} />;
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Close participant actions">
        <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
          <View style={styles.grabber} />
          <Text style={styles.title} numberOfLines={1}>{displayName}</Text>

          {isRemote && (
            <Pressable
              style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
              onPress={toggleMuteForMe}
              accessibilityRole="button"
              accessibilityLabel={mutedForMe ? `Unmute ${displayName} for me` : `Mute ${displayName} for me`}
            >
              <Ionicons name={mutedForMe ? 'volume-high' : 'volume-mute'} size={20} color={colors.white} />
              <Text style={styles.rowText}>{mutedForMe ? 'Unmute for me' : 'Mute for me'}</Text>
            </Pressable>
          )}

          {!!userId && (
            <Pressable
              style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
              onPress={openReport}
              accessibilityRole="button"
              accessibilityLabel={`Report ${displayName}`}
            >
              <Ionicons name="flag-outline" size={20} color={colors.error} />
              <Text style={[styles.rowText, { color: colors.error }]}>Report</Text>
            </Pressable>
          )}

          <Pressable
            style={({ pressed }) => [styles.row, styles.cancelRow, pressed && styles.rowPressed]}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cancel"
          >
            <Text style={styles.cancelText}>Cancel</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#232340',
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxxl,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  grabber: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.25)',
    marginBottom: spacing.md,
  },
  title: {
    fontSize: 16,
    color: colors.white,
    textAlign: 'center',
    marginBottom: spacing.md,
    ...fonts.bold,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  rowPressed: {
    opacity: 0.6,
  },
  rowText: {
    fontSize: 15,
    color: colors.white,
    ...fonts.semibold,
  },
  cancelRow: {
    justifyContent: 'center',
  },
  cancelText: {
    fontSize: 15,
    color: colors.faint,
    ...fonts.semibold,
  },
});
